/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { remoteSessionClientsReadEMA } from "../funcs/remoteSessionClientsReadEMA.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { IdentityChainingPreparation } from "../models/components/identitychainingpreparation.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ReadEMARequest,
  ReadEMASecurity,
} from "../models/operations/readema.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RemoteSessionClientsReadEMAMutationVariables = {
  request: ReadEMARequest;
  security?: ReadEMASecurity | undefined;
  options?: RequestOptions;
};

export type RemoteSessionClientsReadEMAMutationData =
  IdentityChainingPreparation;

export type RemoteSessionClientsReadEMAMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * readEMA remoteSessionClients
 *
 * @remarks
 * Explicit, tenant-scoped identity-chaining configuration. Mutations require project-write authorization; reads require project-read authorization. Does not exchange tokens or establish provider verification, trust, consent, or user access.
 */
export function useRemoteSessionClientsReadEMAMutation(
  options?: MutationHookOptions<
    RemoteSessionClientsReadEMAMutationData,
    RemoteSessionClientsReadEMAMutationError,
    RemoteSessionClientsReadEMAMutationVariables
  >,
): UseMutationResult<
  RemoteSessionClientsReadEMAMutationData,
  RemoteSessionClientsReadEMAMutationError,
  RemoteSessionClientsReadEMAMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRemoteSessionClientsReadEMAMutation(client, options),
    ...options,
  });
}

export function mutationKeyRemoteSessionClientsReadEMA(): MutationKey {
  return ["@gram/client", "remoteSessionClients", "readEMA"];
}

export function buildRemoteSessionClientsReadEMAMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RemoteSessionClientsReadEMAMutationVariables,
  ) => Promise<RemoteSessionClientsReadEMAMutationData>;
} {
  return {
    mutationKey: mutationKeyRemoteSessionClientsReadEMA(),
    mutationFn: function remoteSessionClientsReadEMAMutationFn({
      request,
      security,
      options,
    }): Promise<RemoteSessionClientsReadEMAMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(remoteSessionClientsReadEMA(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
